<script>
// Edits the options of a custom attribute as nested attributes of its form, while the select
// of its type, found by `castTypeInputId`, names one of the `optionCastTypes`. Options
// removed are handed in with `_destroy`, the order as `sort_order`.
export default {
  props: {
    name: { type: String, required: true },
    label: String,
    // Marks the label as required, like simple_form does
    requiredText: String,
    error: String,
    initialOptions: { type: Array, default: () => [] },
    castTypeInputId: { type: String, required: true },
    optionCastTypes: { type: Array, required: true },
  },
  data() {
    const options = this.initialOptions.map((option, index) => ({ key: index, id: option.id, label: option.label, destroy: !!option.destroy }))
    // An empty one to start with
    if (!options.some((option) => !option.destroy)) options.push({ key: options.length, id: null, label: '', destroy: false })
    return { options, nextKey: options.length, castType: null }
  },
  computed: {
    visible() {
      return this.optionCastTypes.includes(this.castType)
    },
    kept() {
      return this.options.filter((option) => !option.destroy)
    },
    removed() {
      return this.options.filter((option) => option.destroy && option.id)
    },
  },
  // The select of the type is only found once mounted: before, Vue builds the page around the
  // component outside the document, as the app takes the page inside #app as its template.
  mounted() {
    this.castTypeInput = document.getElementById(this.castTypeInputId)
    this.castType = this.castTypeInput?.value
    this.castTypeInput?.addEventListener('change', this.changeCastType)
  },
  beforeUnmount() {
    this.castTypeInput?.removeEventListener('change', this.changeCastType)
  },
  methods: {
    changeCastType(event) {
      this.castType = event.target.value
    },
    newOption() {
      return { key: this.nextKey++, id: null, label: '', destroy: false }
    },
    add() {
      const option = this.newOption()
      this.options.push(option)
      this.$nextTick(() => this.$refs[`label-${option.key}`]?.[0]?.focus())
    },
    remove(option) {
      if (option.id) {
        option.destroy = true
      } else {
        this.options.splice(this.options.indexOf(option), 1)
      }
    },
    move(option, by) {
      const other = this.kept[this.kept.indexOf(option) + by]
      const from = this.options.indexOf(option)
      const to = this.options.indexOf(other)
      this.options[from] = other
      this.options[to] = option
    },
  },
};
</script>

<template>
  <!-- Laid out like the inputs of simple_form's horizontal_form wrapper around it -->
  <div v-if="visible" class="row mb-3">
    <label class="col-sm-3 col-form-label">
      {{ label }}
      <abbr v-if="requiredText" :title="requiredText">*</abbr>
    </label>
    <div class="col-sm-9">
      <div v-for="(option, index) in kept" :key="option.key" class="input-group mb-2">
        <input v-if="option.id" type="hidden" :name="`${name}[${index}][id]`" :value="option.id">
        <input type="hidden" :name="`${name}[${index}][sort_order]`" :value="index">
        <input
          type="text"
          class="form-control"
          required
          :ref="`label-${option.key}`"
          :name="`${name}[${index}][label]`"
          :aria-label="label"
          v-model="option.label">
        <button type="button" class="btn btn-outline-secondary" :title="$t('custom_attributes.options.move_up')" :disabled="index == 0" @click="move(option, -1)">
          <span class="bi bi-arrow-up"></span>
        </button>
        <button type="button" class="btn btn-outline-secondary" :title="$t('custom_attributes.options.move_down')" :disabled="index == kept.length - 1" @click="move(option, 1)">
          <span class="bi bi-arrow-down"></span>
        </button>
        <button type="button" class="btn btn-outline-danger" :title="$t('custom_attributes.options.remove')" @click="remove(option)">
          <span class="bi bi-trash"></span>
        </button>
      </div>
      <template v-for="(option, index) in removed" :key="option.key">
        <input type="hidden" :name="`${name}[${kept.length + index}][id]`" :value="option.id">
        <input type="hidden" :name="`${name}[${kept.length + index}][_destroy]`" value="1">
      </template>
      <button type="button" class="btn btn-sm btn-outline-primary" @click="add()">
        <span class="bi bi-plus-lg me-1"></span>{{ $t('custom_attributes.options.add') }}
      </button>
      <div v-if="error" class="invalid-feedback d-block">{{ error }}</div>
    </div>
  </div>
</template>
